/**
 * Confirm push pre/post quiz prompt for sequenced topic lifecycle.
 */

import React, { useEffect, useState } from "react";
import { AlertTriangle, Rocket, X } from "lucide-react";
import type { QuizSlot } from "./liveQuizTypes";
import {
  EMPTY_QUIZ_BANK_MESSAGE,
  fetchLiveQuizAvailability,
  type LiveQuizAvailability,
} from "./liveQuizApi";

type Props = {
  open: boolean;
  slot: QuizSlot;
  topicName: string;
  sessionId?: string;
  topicId?: string;
  busy?: boolean;
  /** Backend push failure, e.g. "Questions doesn't exist". */
  pushError?: string | null;
  onConfirm: () => void;
  onSkip: () => void;
  onClose?: () => void;
};

export function ConfirmPushQuizModal({
  open,
  slot,
  topicName,
  sessionId,
  topicId,
  busy = false,
  pushError = null,
  onConfirm,
  onSkip,
  onClose,
}: Props) {
  const [availability, setAvailability] = useState<LiveQuizAvailability | null>(
    null,
  );
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (!open || !sessionId || !topicId) {
      setAvailability(null);
      setChecking(false);
      return;
    }
    let cancelled = false;
    setChecking(true);
    setAvailability(null);
    void fetchLiveQuizAvailability({ sessionId, topicId, slot })
      .then((preview) => {
        if (!cancelled) setAvailability(preview);
      })
      .catch(() => {
        if (!cancelled) setAvailability(null);
      })
      .finally(() => {
        if (!cancelled) setChecking(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, sessionId, topicId, slot]);

  if (!open) return null;

  const title = slot === "pre" ? "Push pre-quiz?" : "Push post-quiz?";
  const body =
    slot === "pre"
      ? `“${topicName}” has a pre-quiz. Push it to students, or skip to start teaching?`
      : `Mark complete and push the post-quiz for “${topicName}” to students?`;

  const noneAvailable = availability != null && !availability.can_push;
  const shortBank = Boolean(availability?.short);
  const emptyBank =
    noneAvailable ||
    Boolean(pushError && pushError.includes(EMPTY_QUIZ_BANK_MESSAGE));
  const pushDisabled = busy || checking || noneAvailable;

  let warning: string | null = null;
  if (checking) {
    warning = "Checking the question bank…";
  } else if (pushError) {
    warning = pushError.includes(EMPTY_QUIZ_BANK_MESSAGE)
      ? EMPTY_QUIZ_BANK_MESSAGE
      : pushError;
  } else if (noneAvailable) {
    warning = EMPTY_QUIZ_BANK_MESSAGE;
  } else if (shortBank && availability) {
    warning = `Only ${availability.available} of ${availability.needed} questions are available. You can still push the ${availability.available} available question${availability.available === 1 ? "" : "s"}.`;
  }

  return (
    <div
      role="presentation"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 220,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(15, 23, 42, 0.5)",
        backdropFilter: "blur(4px)",
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        style={{
          width: "100%",
          maxWidth: 420,
          borderRadius: 16,
          background: "#fff",
          boxShadow: "0 25px 50px -12px rgba(0,0,0,0.35)",
          padding: 20,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 8,
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: 18,
              fontWeight: 700,
              color: "#0f172a",
            }}
          >
            {title}
          </h2>
          {onClose ? (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              style={{
                border: "none",
                background: "transparent",
                color: "#94a3b8",
                cursor: "pointer",
                padding: 4,
                lineHeight: 0,
              }}
            >
              <X size={18} />
            </button>
          ) : null}
        </div>
        <p
          style={{
            margin: "10px 0 0",
            fontSize: 14,
            color: "#64748b",
            lineHeight: 1.45,
          }}
        >
          {body}
        </p>
        {warning ? (
          <div
            style={{
              marginTop: 12,
              display: "flex",
              alignItems: "flex-start",
              gap: 8,
              borderRadius: 10,
              padding: "10px 12px",
              background: emptyBank
                ? "rgba(239,68,68,0.1)"
                : "rgba(245,158,11,0.12)",
              color: emptyBank ? "#b91c1c" : "#b45309",
              fontSize: 13,
              fontWeight: 600,
              lineHeight: 1.4,
            }}
          >
            <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>{warning}</span>
          </div>
        ) : null}
        <div
          style={{
            marginTop: 18,
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          <button
            type="button"
            disabled={pushDisabled}
            onClick={onConfirm}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              border: "none",
              borderRadius: 12,
              padding: "12px 14px",
              background: pushDisabled ? "#94a3b8" : "#2563eb",
              color: "#fff",
              fontWeight: 700,
              fontSize: 14,
              cursor: pushDisabled
                ? busy
                  ? "wait"
                  : "not-allowed"
                : "pointer",
            }}
          >
            <Rocket size={16} />
            {busy
              ? "Pushing…"
              : slot === "pre"
                ? "Push pre-quiz"
                : "Push post-quiz"}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={onSkip}
            style={{
              border: "2px solid #e2e8f0",
              borderRadius: 12,
              padding: "10px 14px",
              background: "#fff",
              color: "#475569",
              fontWeight: 600,
              fontSize: 13,
              cursor: busy ? "not-allowed" : "pointer",
            }}
          >
            {slot === "pre" ? "Skip pre-quiz" : "Skip post-quiz"}
          </button>
        </div>
      </div>
    </div>
  );
}
